import React, { createContext, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Briefcase, GraduationCap, ArrowRight, UploadSimple, FileText, X } from "@phosphor-icons/react";
import ImageUploadField from "@/pages/admin/ImageUploadField";

const ProSignupCtx = createContext({ open: () => {}, close: () => {} });
export const useProSignup = () => useContext(ProSignupCtx);

export const ProfessionalSignupProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <ProSignupCtx.Provider value={{ open: () => setIsOpen(true), close: () => setIsOpen(false) }}>
      {children}
      <ProSignupDialog isOpen={isOpen} close={() => setIsOpen(false)} />
    </ProSignupCtx.Provider>
  );
};

const csv = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

const initial = {
  professional_type: "therapist",
  name: "", email: "", phone: "",
  title: "", bio: "", photo_url: "",
  experience_years: "", session_fee: "",
  languages: "", specialisations: "", tags: "",
  pathways: "", session_types: "",
  documents: [],
};

const ProSignupDialog = ({ isOpen, close }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => { setForm(initial); setDone(false); };
  const onClose = () => { reset(); close(); };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        professional_type: form.professional_type,
        name: form.name, email: form.email, phone: form.phone,
        title: form.title, bio: form.bio, photo_url: form.photo_url || null,
        experience_years: Number(form.experience_years) || 0,
        session_fee: Number(form.session_fee) || 0,
        languages: csv(form.languages),
        documents: form.documents,
      };
      if (form.professional_type === "therapist") {
        payload.specialisations = csv(form.specialisations);
        payload.tags = csv(form.tags);
      } else {
        payload.pathways = csv(form.pathways);
        payload.session_types = csv(form.session_types);
      }
      await api.post("/professionals/apply", payload);
      setDone(true);
      toast.success("Application received, we'll be in touch soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit application");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="" data-testid="pro-signup-modal">
        <DialogTitle className="sr-only">Apply as a Professional</DialogTitle>
        <DialogDescription className="sr-only">Submit your details to join the MindDhara directory.</DialogDescription>

        {done ? (
          <div className="text-center py-6" data-testid="pro-signup-done">
            <CheckCircle size={48} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto" />
            <h2 className="text-2xl font-serif mt-4">Thanks, application received</h2>
            <p className="mt-2 text-sm" style={{ color: "var(--md-text-soft)" }}>
              Our team will review your profile within 3–5 business days. Check your email for confirmation.
            </p>
            <button onClick={onClose} className="btn-primary mt-6" data-testid="pro-signup-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <div className="md-label-eyebrow mb-2">Join <span className="md-brand">MindDhara</span></div>
              <h2 className="text-2xl md:text-3xl font-serif">Apply as a Professional</h2>
              <p className="mt-2 text-sm" style={{ color: "var(--md-text-soft)" }}>
                A short form. We review every application personally and respond within 3–5 business days.
              </p>
            </div>

            {/* Type pick */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "therapist", label: "Therapist · Couch", icon: Briefcase },
                { id: "mentor", label: "Mentor · Forward", icon: GraduationCap },
              ].map((t) => {
                const active = form.professional_type === t.id;
                return (
                  <button key={t.id} type="button" onClick={() => update("professional_type", t.id)}
                    className="md-card text-left"
                    style={{ padding: "1rem", boxShadow: active ? "0 0 0 2px var(--md-primary)" : "" }}
                    data-testid={`pro-type-${t.id}`}>
                    <t.icon size={22} weight="duotone" style={{ color: "var(--md-primary)" }} />
                    <p className="font-medium text-sm mt-2">{t.label}</p>
                  </button>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input className="md-input" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="pro-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" className="md-input" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="pro-email" />
              </Field>
              <Field label="Phone" required>
                <input type="tel" required minLength={7} className="md-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="pro-phone" />
              </Field>
              <Field label="Title / Designation" required>
                <input className="md-input" required placeholder="e.g. Clinical Psychologist" value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="pro-title" />
              </Field>
              <Field label="Years of experience">
                <input type="number" min="0" className="md-input" value={form.experience_years} onChange={(e) => update("experience_years", e.target.value)} data-testid="pro-experience" />
              </Field>
              <Field label="Session fee (₹)">
                <input type="number" min="0" className="md-input" value={form.session_fee} onChange={(e) => update("session_fee", e.target.value)} data-testid="pro-fee" />
              </Field>
            </div>

            <Field label="Short bio" required>
              <textarea required rows={3} className="md-input" maxLength={600} placeholder="2–3 lines on your approach, training and clients you serve." value={form.bio} onChange={(e) => update("bio", e.target.value)} data-testid="pro-bio" />
            </Field>

            <Field label="Languages (comma-separated)">
              <input className="md-input" placeholder="English, Hindi, Tamil" value={form.languages} onChange={(e) => update("languages", e.target.value)} data-testid="pro-languages" />
            </Field>

            {form.professional_type === "therapist" ? (
              <>
                <Field label="Specialisations (comma-separated)">
                  <input className="md-input" placeholder="Anxiety, Depression, Trauma" value={form.specialisations} onChange={(e) => update("specialisations", e.target.value)} data-testid="pro-specialisations" />
                </Field>
                <Field label="Tags (kebab-case, matches directory filter, e.g. anxiety, focus)">
                  <input className="md-input" placeholder="anxiety, depression, trauma" value={form.tags} onChange={(e) => update("tags", e.target.value)} data-testid="pro-tags" />
                </Field>
              </>
            ) : (
              <>
                <Field label="Pathways (comma-separated)">
                  <input className="md-input" placeholder="DClinPsy, PhD, Career Change" value={form.pathways} onChange={(e) => update("pathways", e.target.value)} data-testid="pro-pathways" />
                </Field>
                <Field label="Session types">
                  <input className="md-input" placeholder="Career Strategy, Mock Interview, Application Review" value={form.session_types} onChange={(e) => update("session_types", e.target.value)} data-testid="pro-session-types" />
                </Field>
              </>
            )}

            <Field label="Degrees & certificates (PDF or image, up to 5)">
              <DocsUploadField
                docs={form.documents}
                onChange={(docs) => update("documents", docs)}
              />
            </Field>

            <Field label="Profile photo (optional)">
              <ImageUploadField
                value={form.photo_url}
                onChange={(v) => update("photo_url", v)}
                endpoint="/uploads/public-image"
                testid="pro-photo"
              />
            </Field>

            <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--md-border)" }}>
              <button type="button" onClick={onClose} className="btn-outline text-sm" data-testid="pro-cancel">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="pro-submit">
                {busy ? "Submitting…" : "Submit Application"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

function DocsUploadField({ docs, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (docs.length + files.length > 5) {
      toast.error("You can upload up to 5 documents");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name} is too large (max 10 MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads/public-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ name: f.name, url: r.data.url });
      }
      if (uploaded.length) {
        onChange([...docs, ...uploaded]);
        toast.success(`${uploaded.length} document${uploaded.length > 1 ? "s" : ""} uploaded`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Document upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="pro-docs-field">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy || docs.length >= 5}
        className="btn-outline text-sm"
        data-testid="pro-docs-upload-btn"
      >
        <UploadSimple size={14} weight="bold" /> {busy ? "Uploading…" : "Upload documents"}
      </button>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
        onChange={onPick}
        style={{ display: "none" }}
        data-testid="pro-docs-input"
      />
      {docs.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="pro-docs-list">
          {docs.map((d, i) => (
            <li key={d.url} className="flex items-center gap-2 text-xs px-3 py-2 rounded" style={{ background: "var(--md-bg-alt)" }} data-testid={`pro-doc-item-${i}`}>
              <FileText size={14} weight="duotone" style={{ color: "var(--md-primary)", flexShrink: 0 }} />
              <span className="truncate flex-1">{d.name}</span>
              <button type="button" onClick={() => onChange(docs.filter((_, j) => j !== i))} aria-label={`Remove ${d.name}`} data-testid={`pro-doc-remove-${i}`}>
                <X size={13} weight="bold" style={{ color: "var(--md-text-muted)" }} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="md-label-eyebrow block mb-1.5">{label}{required && " *"}</label>
      {children}
    </div>
  );
}
